
import { useEffect, useState } from 'react'
import { makeRequest } from '@helpers/axios-manipulator';
import { Button, Card, Col, Empty, Row, Skeleton, Tag, Tooltip, message, Segmented, Input, Switch, Typography, Modal, Select, Table, Spin } from 'antd';
import { SendOutlined, ReloadOutlined } from '@ant-design/icons';
import React from 'react';
import { OnboardingUsers } from '../../../data/models/onboarding_users';
import * as OnboardingStyles from './styles';
import emailjs from 'emailjs-com';
import UserSelect from '../../components/menu/Form/select/UserSelect';
import { User } from '@models/user';

const { Text } = Typography;

const EMAILJS_SERVICE_ID = "service_bohk2ny";
const EMAILJS_PUBLIC_KEY = 'zqjDUmERbRJjHE37w';

const DOMAIN_SEGMENT_OPTIONS = [
  {
    label: (
      <div style={{ padding: 0 }}>
        <p>DEV</p>
      </div>
    ),
    value: 'https://dev.ujamaa.digital/',
  },
  {
    label: (
      <div style={{ padding: 0 }}>
        <p style={{ color: "goldenrod" }}>QA</p>
      </div>
    ),
    value: 'https://qa.ujamaa.digital/',
  },
  {
    label: (
      <div style={{ padding: 0 }}>
        <p style={{ color: "purple" }}>PROD</p>
      </div>
    ),
    value: 'https://app.ujamaa.digital/',
  },
];

const DOMAIN_LIST = {
  "http://localhost:8080": "https://dev.ujamaa.digital/",
  "https://bizdev.ujamaa.digital": "https://dev.ujamaa.digital/",
  "https://bizqa.ujamaa.digital": "https://qa.ujamaa.digital/",
  "https://biz.ujamaa.digital": "https://app.ujamaa.digital/",
}

const Onboarding = () => {
  const [users, setUsers] = useState<OnboardingUsers[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showOnboarded, setShowOnboarded] = useState<boolean>(false);
  const [showEmailSent, setShowEmailSent] = useState<boolean>(true);
  const [showIncomplete, setShowIncomplete] = useState<boolean>(true);
  const [searchText, setSearchText] = useState('');
  const [emailCC, setEmailCC] = useState(''); // State to store the input value
  const [emailBCC, setEmailBCC] = useState(''); // State to store the input value
  const [userData, setUserData] = useState<User>({} as User);
  const [openNewInvite, setOpenNewInvite] = useState<boolean>(false);
  const [businessOptions, setBusinessOptions] = useState<any[]>([]);
  const [selectedBusiness, setSelectedBusiness] = useState<Record<string, string>>({});
  const [businessLoading, setBusinessLoading] = useState(false);

  // Template ID state for each user row
  const [templateIds, setTemplateIds] = useState<Record<string, string | null>>({});

  // Fetch business options for dropdown
  useEffect(() => {
    const fetchBusinesses = async () => {
      setBusinessLoading(true);
      try {
        const response = await makeRequest({
          origin: window.location.origin,
          method: 'get',
          url: '/api-billing-search',
          headers: {},
        });
        setBusinessOptions(
          (response || []).map((item: any) => ({
            label: item.display_name || item.contact_email || item.business_id,
            value: item.business_id,
          }))
        );
      } catch (error) {
        setBusinessOptions([]);
      } finally {
        setBusinessLoading(false);
      }
    };
    fetchBusinesses();
  }, []);

  // Update templateIds when selectedBusiness or users change
  useEffect(() => {
    const updateTemplateIds = async () => {
      const newTemplateIds: Record<string, string | null> = {};
      await Promise.all(users.map(async (user) => {
        const businessId = selectedBusiness[user.id] || user.rep_id;
        if (businessId) {
          const templateId = await getOnboardingTemplate(businessId);
          newTemplateIds[user.id] = templateId;
        } else {
          newTemplateIds[user.id] = null;
        }
      }));
      setTemplateIds(newTemplateIds);
    };
    if (users.length > 0) {
      updateTemplateIds();
    }
  }, [selectedBusiness, users]);

  // Handler to capture user input
  const handleCCInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailCC(e.target.value);
  };

  const handleBCCInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailBCC(e.target.value);
  };

  if (window.location.origin in DOMAIN_LIST) {
  // ...existing code...
  } else {
  // ...existing code...
  }
  const fetchOnboardingUsers = async () => {
    try {
      const response = await makeRequest({
        origin: window.location.origin,
        method: 'get',
        url: '/api-onboarding',
        headers: {},
      });

      const serverUsers = response; // Assuming response.data is the array of rows from the server

      // This was for testing...
      /*
      const numberOfUsers = serverUsers.length;

      
      // Calculate how many more rows are needed to reach 10
      const additionalUsersNeeded = Math.max(0, 10 - numberOfUsers);

      // Create additional rows if needed
      const additionalUsers = Array(additionalUsersNeeded)
        .fill(serverUsers[0]) // Use the first user as the base
        .map((user, index) => ({
          ...user, // Spread the original user properties
          notification_email: `simulated${index + 1}@example.com`, // Assign unique email addresses
        }));

      // Combine the server users with the additional simulated users
      const allUsers = [...serverUsers, ...additionalUsers];
      */
      const allUsers = [...serverUsers];

      setUsers(allUsers);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    fetchOnboardingUsers();
  }, []);

const getOnboardingTemplate = async (businessId: string) => {
    try {
        const response = await makeRequest({
            origin: window.location.origin,
            method: 'get',
            url: `/api/client-admin-properties/${businessId}`,
            headers: {},
        });
        const templateProp = response.find((prop: any) => prop.prop_code === 'onboarding-template-id');
        return templateProp ? templateProp.prop_value : null;
    } catch (error) {
        console.error('Error fetching onboarding template:', error);
        return null;
    }
};

const sendNotificationEmail = async (e: React.MouseEvent<any> | null, data: any) => {
  if (e) e.preventDefault();
  message.loading(`Sending an email to notify user ${data.username}`);

  const { id, username, notification_email, rep_id } = data;
  const businessId = rep_id;

    if (!businessId) {
    message.error('Please select a business before sending notification.');
    return;
  }

  let templateId = null;
  try {
    const customTemplateId = await getOnboardingTemplate(businessId);
    if (customTemplateId) {
      templateId = customTemplateId;
    } else {
      message.error('No custom template found for this business. Cannot send notification.');
      return;
    }
  } catch (err) {
    message.error('Error fetching template for selected business.');
    return;
  }

  // Debug: show all EmailJS parameters
  const originKey = window.location.origin as keyof typeof DOMAIN_LIST;
  const weblink = `${DOMAIN_LIST[originKey] ?? ''}${username}?onboardingId=${id}&notificationEmail=${notification_email}`;
  const emailParams = {
    send_to: notification_email,
    Weblink: weblink,
    send_cc: emailCC,
    send_bcc: emailBCC,
  };

  message.info(`Using template: ${templateId}`);

  emailjs
    .send(
      EMAILJS_SERVICE_ID,
      templateId,
      emailParams,
      EMAILJS_PUBLIC_KEY,
    ).then(
      async (result) => {
        message.success('Email sent successfully!');
        setOpenNewInvite(false);
        setUserData({} as User)
        const response = await makeRequest({
          origin: window.location.origin,
          method: 'post',
          url: '/api-onboarding',
          headers: { 'Content-Type': 'application/json' },
          data: {
            id: id,
            notification_status: 'SENT',
          },
        });

        if (response && response.success) {
          fetchOnboardingUsers();
        }
      },
      (error) => {
        console.error('Error sending email:', error);
        message.error('Failed to send email.');
      }
    );
}

  const refreshData = async () => {
    setLoading(true);
    await fetchOnboardingUsers();
    setLoading(false);
  }

  const columns = [
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
      sorter: (a: OnboardingUsers, b: OnboardingUsers) => (a.username || "").localeCompare(b.username || ""),
    },
    {
      title: 'User ID',
      dataIndex: 'userid',
      key: 'userid',
    },
    {
      title: 'Account Type',
      dataIndex: 'account_type',
      key: 'account_type',
    },
    {
      title: 'Notification Email',
      dataIndex: 'notification_email',
      key: 'notification_email',
    },
    {
      title: 'Template ID',
      key: 'template_id',
      render: (_: any, record: OnboardingUsers) => (
        <span>{templateIds[record.id] || '-'}</span>
      ),
    },
    {
      title: 'Business',
      key: 'business',
      render: (_: any, record: OnboardingUsers) => (
        <Select
          showSearch
          placeholder="Select business"
          value={selectedBusiness[record.id] || record.rep_id || undefined}
          onChange={val => setSelectedBusiness(prev => ({ ...prev, [record.id]: val }))}
          options={businessOptions}
          style={{ minWidth: 160 }}
          loading={businessLoading}
        />
      ),
    },
    {
      title: 'Status',
      dataIndex: 'notification_status',
      key: 'notification_status',
      render: (status: string) => {
        let color = 'default';
        let text = status;
        if (status === 'OPEN') {
          color = 'orange';
          text = 'Incomplete';
        } else if (status === 'SENT') {
          color = 'green';
          text = 'Email Sent';
        } else if (status === 'COMPLETE') {
          color = 'purple';
          text = 'ONBOARDED';
        }
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: OnboardingUsers) => (
        record.is_active ? (
          <Tooltip title="Send Onboarding Email">
            <Button onClick={(e) => sendNotificationEmail(e, { ...record, rep_id: selectedBusiness[record.id] || record.rep_id })}>
              <SendOutlined />
            </Button>
          </Tooltip>
        ) : null
      ),
    },
  ];

  return (
    <>

      <div className='mt-5' style={{ height: '100%' }}>
              <Button type="primary" style={{ float: "right", marginTop: "-2.5rem", padding: ".5rem 1.5rem", height: "auto" }}
        onClick={() => setOpenNewInvite(true)}
      >Re-invite</Button>
        <Card
          title={`Onboard Users | ${users.filter(item => item.notification_status === 'OPEN').length} Not Notified`}
          style={{ backgroundColor: "#e0e0e0" }}
          extra={<Tooltip title="Refresh">
            <Button shape='circle' style={{ backgroundColor: "#f0f0f0" }} onClick={refreshData}>
              <ReloadOutlined style={{ color: "blue" }} />
            </Button>
          </Tooltip>}
        >
          <Row gutter={8}>
            <Col span={4}>
              {/* <Segmented
              onChange={onDomainChanged}
              options={DOMAIN_SEGMENT_OPTIONS}
            /> */}
              <p className='mb-0' style={{ fontSize: "12px" }}>Domain:
              </p>
<b>{DOMAIN_LIST[window.location.origin as keyof typeof DOMAIN_LIST] ?? ''}</b>
            </Col>
            <Col span={24} md={10}>
              <div>
                <span>CC:</span>
                <Input allowClear onChange={handleCCInputChange} />
              </div>
            </Col>
            <Col span={24} md={10}>
              <div>
                <span>BCC:</span>
                <Input allowClear onChange={handleBCCInputChange} />
              </div>
            </Col>
          </Row>

          <div style={{ height: '0.5rem' }} />

          <hr />
          <Row gutter={[16, 8]} align="bottom" style={{ marginBottom: 16 }}>
            <Col>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Text>Show Onboarded</Text>
                <Switch checked={showOnboarded} onChange={setShowOnboarded} />
              </div>
            </Col>
            <Col>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Text>Show Email Sent</Text>
                <Switch checked={showEmailSent} onChange={setShowEmailSent} />
              </div>
            </Col>
            <Col>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Text>Show Incomplete</Text>
                <Switch checked={showIncomplete} onChange={setShowIncomplete} />
              </div>
            </Col>
            <Col flex="auto">
              <Input
                placeholder="Search by username or email"
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                allowClear
              />
            </Col>
          </Row>

          <Table
            dataSource={users.filter(user => {
              const searchMatch = !searchText ||
                (user.username || '').toLowerCase().includes(searchText.toLowerCase()) ||
                (user.notification_email || '').toLowerCase().includes(searchText.toLowerCase());

              if (!searchMatch) return false;

              if (user.notification_status === 'COMPLETE' && !showOnboarded) return false;
              if (user.notification_status === 'SENT' && !showEmailSent) return false;
              if (user.notification_status === 'OPEN' && !showIncomplete) return false;

              return true;
            })}
            columns={columns}
            loading={loading}
            rowKey="id"
          />
        </Card>
        {openNewInvite &&
          <Modal
            centered
            title="Send invitation"
            open={openNewInvite}
            okText="send"
            onOk={() => sendNotificationEmail(null, userData)}
            okButtonProps={{ disabled: userData && Object.keys(userData)?.length === 0 }}
            onCancel={() => { setOpenNewInvite(false); setUserData({} as User) }}
            width={375}
          >
            <UserSelect route='/api-onboarding/availables' update={loading} className='mb-5' setData={setUserData} data={userData} />
          </Modal>
        }
      </div>
    </>
  )
}

export default Onboarding;

